import { useMemo, useId, useCallback } from 'react'
import styled from '@emotion/styled'

import { UiIcon } from '../Icon'
import { errorStyles, inputStyles } from 'css/utils'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { RefCallBack } from 'react-hook-form'
import { InputContainer, InputContainerProps } from 'modules/ui/atoms/Input/InputContainer'
import { convertRgbOrRgbaToHex } from '../../../../utils/colors'

export interface SelectProps extends InputContainerProps {
  onChange: any
  onBlur?: () => void
  inputRef?: RefCallBack
  options: Array<
    | string
    | {
        label: string
        value: string
      }
  >
  autoComplete?: string
  dataId?: string
  autoFocus?: boolean
}

type StyledSelectItem = {
  placeholder?: string
  errored: boolean
}

const StyledSelect = styled.select<StyledSelectItem>(
  {
    background: 'transparent !important',
    appearance: 'none',
    boxShadow: '0px 2px 2px rgba(27, 25, 24, 0.12)',
    cursor: 'pointer',
    minHeight: '40px',
    ...inputStyles,
    '&.unselected': {
      color: DEFAULT_THEME_COLORS['--theme-color--neutral-45'],
    },
    '&:focus': {
      outline: 'none', // Remove outline on focus
    },
  },
  errorStyles,
)

export const SelectInput = (props: SelectProps & { customStyles?: React.CSSProperties }) => {
  const {
    onChange,
    onBlur,
    inputRef,
    value,
    placeholder,
    options,
    error,
    autoComplete,
    dataId = '',
    autoFocus,
    customStyles,
  } = props

  const captionId = useId()

  const structuredOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option == 'string' ? { label: option, value: option } : option,
      ),
    [options],
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeCallback = useCallback(
    (e: { target: { value: string } }) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  return (
    <InputContainer {...props} captionId={captionId} hideErrorCaption>
      <StyledSelect
        style={customStyles}
        className={placeholder && value === '' ? 'unselected' : ''}
        onChange={onChangeCallback}
        onBlur={onBlur}
        ref={inputRef}
        errored={!!error}
        aria-invalid={!!error ? 'true' : 'false'}
        value={value}
        autoComplete={autoComplete}
        id={props.id}
        data-id={dataId}
        autoFocus={autoFocus}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {structuredOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </StyledSelect>

      <div
        style={{
          position: 'absolute',
          right: '10px',
          top: '12px',
          pointerEvents: 'none',
          background: convertRgbOrRgbaToHex(customStyles?.background as string),
        }}
      >
        <UiIcon name="arrow_drop_down" />
      </div>
    </InputContainer>
  )
}
